// The dates an invoice may carry: the last two months, from the same day two
// months back up to today (on 20 Oct 2026: 20 Aug 2026 - 20 Oct 2026). A
// future date is refused. Two months back from a day the earlier month does
// not have is that month's last day: 30 Apr -> 28 Feb (29 in a leap year).
//
// Dates are this computer's local dates. (They used to be cut from
// toISOString(), which is UTC: before 5 AM in Pakistan that gave yesterday.)
//
// Mirrors services/invoiceDate.js in the backend, which checks it again, and
// src/utils/invoiceDate.ts in the app.

const MONTHS_BACK = 2;
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const pad = (n) => String(n).padStart(2, "0");
// `month` is 0-based, as Date has it.
const ymd = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;
const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

/** { min, max } as YYYY-MM-DD, for `now` (default: this moment). */
export const invoiceDateRange = (now = new Date()) => {
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  let fromYear = year;
  let fromMonth = month - MONTHS_BACK;
  if (fromMonth < 0) {
    fromMonth += 12;
    fromYear -= 1;
  }
  return {
    min: ymd(fromYear, fromMonth, Math.min(day, daysInMonth(fromYear, fromMonth))),
    max: ymd(year, month, day),
  };
};

const isRealDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day;
};

const shown = (value) => {
  const [year, month, day] = value.split("-");
  return `${day} ${MONTH_NAMES[Number(month) - 1]} ${year}`;
};

/** Why `date` cannot be an invoice's date, or null when it can. */
export const invoiceDateError = (date, now = new Date()) => {
  const value = String(date ?? "").trim();
  if (!isRealDate(value)) return "Invoice date is required.";
  const { min, max } = invoiceDateRange(now);
  if (value < min || value > max) {
    return `Invoice date must be within the last 2 months: from ${shown(min)} to ${shown(max)}.`;
  }
  return null;
};
